import React, { useState, useEffect } from 'react';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

const MechanicManagement = ({ user, setUser }) => {
  const navigate = useNavigate();
  const [mechanics, setMechanics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [selectedMechanic, setSelectedMechanic] = useState(null);
  const [formData, setFormData] = useState({
    nombre: '',
    email: '',
    password: ''
  });
  const [editFormData, setEditFormData] = useState({
    nombre: '',
    email: ''
  });
  const [submitting, setSubmitting] = useState(false);

  const fetchMechanics = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/admin/mechanics`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const data = await response.json();
      if (response.ok) {
        setMechanics(data);
      }
    } catch (error) {
      console.error('Error fetching mechanics:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMechanics();
  }, []);

  const handleLogout = () => {
    localStorage.removeItem('token');
    setUser(null);
  };

  const handleCreateMechanic = async () => {
    if (!formData.nombre || !formData.email || !formData.password) {
      toast.error('Por favor completa todos los campos');
      return;
    }

    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/admin/mechanics`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...formData,
          rol: 'MECANICO'
        })
      });

      const data = await response.json();

      if (response.ok) {
        toast.success('Mecánico creado exitosamente');
        setShowCreateDialog(false);
        setFormData({ nombre: '', email: '', password: '' });
        fetchMechanics();
      } else {
        toast.error(data.detail || 'Error al crear mecánico');
      }
    } catch (error) {
      toast.error('Error de conexión');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditMechanic = async () => {
    if (!editFormData.nombre || !editFormData.email) {
      toast.error('Por favor completa todos los campos');
      return;
    }

    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/admin/mechanics/${selectedMechanic.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(editFormData)
      });

      if (response.ok) {
        toast.success('Mecánico actualizado exitosamente');
        setShowEditDialog(false);
        setSelectedMechanic(null);
        setEditFormData({ nombre: '', email: '' });
        fetchMechanics();
      } else {
        const data = await response.json();
        toast.error(data.detail || 'Error al actualizar mecánico');
      }
    } catch (error) {
      toast.error('Error de conexión');
    } finally {
      setSubmitting(false);
    }
  };

  const handleOpenEdit = (mechanic) => {
    setSelectedMechanic(mechanic);
    setEditFormData({
      nombre: mechanic.nombre,
      email: mechanic.email
    });
    setShowEditDialog(true);
  };

  const handleToggleActive = async (mechanicId, currentStatus) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/admin/mechanics/${mechanicId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          activo: !currentStatus
        })
      });

      if (response.ok) {
        toast.success('Estado actualizado');
        fetchMechanics();
      } else {
        const data = await response.json();
        toast.error(data.detail || 'Error al actualizar');
      }
    } catch (error) {
      toast.error('Error de conexión');
    }
  };

  const handleDeleteMechanic = async (mechanicId) => {
    if (!window.confirm('¿Estás seguro de que deseas desactivar este mecánico?')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/admin/mechanics/${mechanicId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        toast.success('Mecánico desactivado');
        fetchMechanics();
      } else {
        const data = await response.json();
        toast.error(data.detail || 'Error al desactivar');
      }
    } catch (error) {
      toast.error('Error de conexión');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-black">
        <div className="text-red-600 text-xl">Cargando...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black">
      {/* Header */}
      <header className="bg-zinc-900 border-b border-zinc-800 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-4">
              <div className="bg-red-600 p-3 rounded-xl">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              </div>
              <div>
                <h1 className="text-xl font-bold text-white">Gestión de Mecánicos</h1>
                <p className="text-sm text-gray-400">Administración CRUD Completa</p>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <Button
                onClick={() => navigate('/admin/dashboard')}
                variant="outline"
                className="border-zinc-700 text-gray-300 hover:bg-zinc-800"
                data-testid="back-to-dashboard-button"
              >
                ← Volver al Tablero
              </Button>
              <Button
                onClick={handleLogout}
                variant="outline"
                className="border-zinc-700 text-gray-300 hover:bg-zinc-800"
                data-testid="logout-button"
              >
                Salir
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex justify-between items-center mb-8">
          <h2 className="text-3xl font-bold text-white">Mecánicos Registrados</h2>
          <Button
            onClick={() => setShowCreateDialog(true)}
            className="bg-red-600 hover:bg-red-700 text-white font-semibold px-6 py-6 text-lg rounded-xl"
            data-testid="create-mechanic-button"
          >
            + Crear Mecánico
          </Button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="mechanics-list">
          {mechanics.map((mech) => (
            <div
              key={mech.id}
              className="bg-zinc-900 rounded-2xl p-6 border border-zinc-800 shadow-lg"
              data-testid={`mechanic-card-${mech.id}`}
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <h3 className="text-white text-xl font-bold mb-1">{mech.nombre}</h3>
                  <p className="text-gray-400 text-sm">{mech.email}</p>
                </div>
                <div className={`px-3 py-1 rounded-full text-xs font-semibold ${
                  mech.activo ? 'bg-green-600/20 text-green-400 border border-green-600' : 'bg-gray-600/20 text-gray-400 border border-gray-600'
                }`}>
                  {mech.activo ? 'Activo' : 'Inactivo'}
                </div>
              </div>

              <div className="space-y-2">
                <Button
                  onClick={() => handleOpenEdit(mech)}
                  variant="outline"
                  className="w-full border-zinc-700 text-gray-300 hover:bg-zinc-800 hover:border-blue-600"
                  data-testid={`edit-mechanic-${mech.id}`}
                >
                  ✏️ Editar
                </Button>
                <Button
                  onClick={() => handleToggleActive(mech.id, mech.activo)}
                  variant="outline"
                  className="w-full border-zinc-700 text-gray-300 hover:bg-zinc-800"
                  data-testid={`toggle-active-${mech.id}`}
                >
                  {mech.activo ? '🔴 Desactivar' : '🟢 Activar'}
                </Button>
                {mech.activo && (
                  <Button
                    onClick={() => handleDeleteMechanic(mech.id)}
                    variant="outline"
                    className="w-full border-zinc-700 text-red-400 hover:bg-red-900/20 hover:border-red-600"
                    data-testid={`delete-mechanic-${mech.id}`}
                  >
                    🗑️ Eliminar
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      </main>

      {/* Create Mechanic Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-white max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold">Crear Nuevo Mecánico</DialogTitle>
          </DialogHeader>
          <div className="space-y-6 py-4">
            <div className="space-y-2">
              <Label htmlFor="nombre">Nombre completo</Label>
              <Input
                id="nombre"
                value={formData.nombre}
                onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
                placeholder="Ej: Carlos Rodríguez"
                className="bg-zinc-800 border-zinc-700 text-white"
                data-testid="create-mechanic-name"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="carlos@melgoza.com"
                className="bg-zinc-800 border-zinc-700 text-white"
                data-testid="create-mechanic-email"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">Contraseña inicial</Label>
              <Input
                id="password"
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                placeholder="••••••••"
                className="bg-zinc-800 border-zinc-700 text-white"
                data-testid="create-mechanic-password"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              onClick={() => setShowCreateDialog(false)}
              variant="outline"
              className="border-zinc-700 text-gray-300"
              data-testid="cancel-create-button"
            >
              Cancelar
            </Button>
            <Button
              onClick={handleCreateMechanic}
              disabled={submitting}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="confirm-create-button"
            >
              {submitting ? 'Creando...' : 'Crear Mecánico'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Mechanic Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-white max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold">Editar Mecánico</DialogTitle>
          </DialogHeader>
          <div className="space-y-6 py-4">
            <div className="space-y-2">
              <Label htmlFor="edit-nombre">Nombre completo</Label>
              <Input
                id="edit-nombre"
                value={editFormData.nombre}
                onChange={(e) => setEditFormData({ ...editFormData, nombre: e.target.value })}
                placeholder="Ej: Carlos Rodríguez"
                className="bg-zinc-800 border-zinc-700 text-white"
                data-testid="edit-mechanic-name"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit-email">Email</Label>
              <Input
                id="edit-email"
                type="email"
                value={editFormData.email}
                onChange={(e) => setEditFormData({ ...editFormData, email: e.target.value })}
                placeholder="carlos@melgoza.com"
                className="bg-zinc-800 border-zinc-700 text-white"
                data-testid="edit-mechanic-email"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              onClick={() => setShowEditDialog(false)}
              variant="outline"
              className="border-zinc-700 text-gray-300"
              data-testid="cancel-edit-button"
            >
              Cancelar
            </Button>
            <Button
              onClick={handleEditMechanic}
              disabled={submitting}
              className="bg-blue-600 hover:bg-blue-700 text-white"
              data-testid="confirm-edit-button"
            >
              {submitting ? 'Actualizando...' : 'Guardar Cambios'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MechanicManagement;
